import logo from "./logo.svg";
import "./App.css";
import LayoutsRoutes from "./Routes/LayoutsRoutes";
import ParentalPasscodeModal from "./Components/Modals/ParentalPasscodeModal";
import "swiper/css";
import "swiper/css/navigation";
import '@fortawesome/fontawesome-free/css/all.min.css';
import * as service from "./network/service";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProjectInfo } from "./Redux/ProjectInfo/ProjectInfoSlice";
import { getAccessToken } from "./Redux/AToken/ATokenSlice";
import Fingerprint2 from "fingerprintjs2";
import { checkOperatingSystem, getSessionId, handleChangeColor } from "./utils/utils";
import { handleFontFamily } from "./utils/fontConfig";
import { getUser } from "./Redux/UserSlice/UserSlice";
import { getUserDetails } from "./Redux/UserDetailsSlice/UserDetailsSlice";
import { getSessionIdValue } from "./Redux/SessionId/sessionIdSlice";
import { toast } from "react-toastify";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Loading from "./Components/Loading/Loading";
import { GoogleOAuthProvider } from '@react-oauth/google';
import NotFound from "./Screens/NotFound/NotFound";
import TopBanner from "./Components/TopBanner/TopBanner";
import { useNavigate } from "react-router-dom";
import i18n from 'i18next';
import { getLanguageModal } from "./Redux/LanguageModal/languageModalSlice";
import {
  setTurnstileTokenValue,
  getTurnstileTokenValue,
  clearTurnstileTokenValue,
  isTurnstileBootstrapPresent,
} from "./network/turnstileToken";

const TURNSTILE_LOAD_TIMEOUT_MS = 60000;
const APP_INFO_MAX_RETRIES = 3;

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const languageModal = useSelector(
    (state) => state?.languageModal?.value

  );
  const accessTokenKids = useSelector((state) => state?.accessToken?.value);
  const [isAuthenticated, SetIsAuthenticated] = useState(false);
  const [projectInfo, setProjectInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [showParentalSetupModal, setShowParentalSetupModal] = useState(false);
  const parentalPinCreatedRef = useRef(false);
  const [scroll, setScroll] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileFailed, setTurnstileFailed] = useState(false);
  const turnstileWidgetId = useRef(null);
  // Capture once: tenants without Turnstile bootstrap skip the wait gate
  const turnstileRequired = useRef(isTurnstileBootstrapPresent()).current;
  const appInfoFetched = useRef(false);
  const appInfoSucceeded = useRef(false);
  const appInfoRetryCount = useRef(0);
  const location = useLocation();
  const urlParams = new URLSearchParams(window.location.search);
  const loginTicket = urlParams.get("ticket");
  const affliateId = urlParams.get("af_id");
  const androidToken = urlParams.get("antkn");
  const notFound = urlParams.get("nf")
  const s2s_transaction_id = urlParams.get("sub1");

  const user = localStorage.getItem("userId");
  const qr = urlParams.get("qr");

  const appInfo = {
    projectDetails: projectInfo,
  };

  // --- Cloudflare Turnstile: only when early HTML bootstrap is present ---
  useEffect(() => {
    if (!turnstileRequired) return;

    let loadTimeoutId;
    let tokenTimeoutId;
    let cancelled = false;

    const failTurnstile = (message) => {
      if (cancelled) return;
      clearTurnstileTokenValue();
      setTurnstileToken(null);
      setTurnstileFailed(true);
      setLoading(false);
      toast.error(message, { position: "bottom-center" });
    };

    const onToken = (token) => {
      if (cancelled || !token) return;
      clearTimeout(tokenTimeoutId);
      clearTimeout(loadTimeoutId);
      setTurnstileFailed(false);
      setTurnstileTokenValue(token);
      setTurnstileToken(token);
    };

    const onExpired = () => {
      clearTurnstileTokenValue();
      setTurnstileToken(null);
      if (!appInfoSucceeded.current) {
        appInfoFetched.current = false;
      }
    };

    // Receive tokens from the early HTML bootstrap (runs before React)
    window.__turnstileAppOnToken = onToken;
    window.__turnstileAppOnExpired = onExpired;

    if (window.__turnstileWidgetId != null) {
      turnstileWidgetId.current = window.__turnstileWidgetId;
    }

    if (window.__turnstileToken) {
      onToken(window.__turnstileToken);
    } else if (Array.isArray(window.__turnstileTokenWaiters)) {
      window.__turnstileTokenWaiters.push(onToken);
    }

    // Fallback if early bootstrap has not rendered yet
    window.__turnstileAppInit = () => {
      if (typeof window.__startTurnstileEarly === "function") {
        window.__startTurnstileEarly();
      }
      if (window.__turnstileWidgetId != null) {
        turnstileWidgetId.current = window.__turnstileWidgetId;
      }
    };

    if (window.turnstile || window.__turnstileApiReady) {
      window.__turnstileAppInit();
    }

    tokenTimeoutId = setTimeout(() => {
      if (!getTurnstileTokenValue() && !appInfoSucceeded.current) {
        failTurnstile(
          "Security verification timed out. Please refresh and try again."
        );
      }
    }, TURNSTILE_LOAD_TIMEOUT_MS);

    loadTimeoutId = setTimeout(() => {
      if (
        window.__turnstileWidgetId == null &&
        turnstileWidgetId.current == null &&
        !getTurnstileTokenValue()
      ) {
        failTurnstile(
          "Security verification could not load. Please disable blockers or refresh."
        );
      }
    }, TURNSTILE_LOAD_TIMEOUT_MS);

    return () => {
      cancelled = true;
      clearTimeout(loadTimeoutId);
      clearTimeout(tokenTimeoutId);
      if (window.__turnstileAppOnToken === onToken) {
        window.__turnstileAppOnToken = undefined;
      }
      if (window.__turnstileAppOnExpired === onExpired) {
        window.__turnstileAppOnExpired = undefined;
      }
      // Keep the page-level widget alive — do not remove on App effect cleanup
    };
  }, [turnstileRequired]);

  // Trigger app init: wait for Turnstile token when bootstrap is present; otherwise boot immediately
  useEffect(() => {
    if (turnstileRequired && !turnstileToken) return;
    if (appInfoFetched.current || turnstileFailed) {
      return;
    }
    appInfoFetched.current = true;
    if (affliateId) {
      localStorage.setItem("affliateId", affliateId);
      localStorage.setItem("affliateUrl", window.location.href);
      if (checkOperatingSystem() === "android" || checkOperatingSystem() === "iPhone") {
        localStorage.setItem(
          "affliateRedirectPath",
          location?.pathname
        );
      }
    }
    if (s2s_transaction_id) {
      localStorage.setItem("s2s_transaction_id", s2s_transaction_id);
      navigate(location?.pathname)
    }
    let info = {};
    setLoading(true);
    if (user) {
      dispatch(
        getUser({
          user: user,
        })
      );
    }

    getAppInfo();
  }, [turnstileToken, turnstileFailed]);
  // useEffect(() => {
  //   if (Object.keys(projectInfo)?.length > 0) {
  //     if (loginTicket) {
  //       externalWebsiteLogin()
  //     }
  //     else if (!localStorage.getItem("gid")) {
  //       getGuestUser();
  //     } else {
  //       getAuthentication();
  //     }
  //   }
  // }, [projectInfo]);
  useEffect(() => {

    // Initialize i18next with a default language
    i18n.init({
      lng: languageModal?.selectedLanguageCode,
      resources: {},
    });
    let progressBarHandler = () => {

      const totalScroll = document.documentElement.scrollTop;
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolls = `${totalScroll / windowHeight}`;

      setScroll(scrolls);
    }
    window.addEventListener("scroll", progressBarHandler);
    return () => window.removeEventListener("scroll", progressBarHandler);

  }, []);

  const storedData = localStorage.getItem('selectedLanguageId');
  const languagestored = localStorage.getItem('selectedLanguageName');
  const storedLanguage = localStorage.getItem('selectedLanguageCode');

  useEffect(() => {
    const storedLanguage = localStorage.getItem('selectedLanguageCode');
    if (storedLanguage) {

      dispatch(getLanguageModal({ languageModal: { ...languageModal, selectedLanguageCode: storedLanguage } }));
      i18n.changeLanguage(storedLanguage);
    } else {
      console.log("No language in localStorage");

    }
  }, []);
  useEffect(() => {
    const pubId = projectInfo?.projectConfig?.pubid || projectInfo?.pubid;
    if (pubId) {
      document.body.classList.add(`pubid-${pubId}`);
      if (Number(pubId) === 50187) {
        document.body.classList.add("yowtv", "pubid-50187");
      }
    }
    handleChangeColor(projectInfo?.projectConfig?.config); // this will handle the template color codes
    handleFontFamily(projectInfo?.projectConfig?.config, projectInfo?.projectConfig?.pubid, "Vaud");
    if (loginTicket) {
      externalWebsiteLogin()
    } else if (Object.keys(projectInfo)?.length > 0) {
      if (!localStorage.getItem("gid")) {
        getGuestUser();
      } else {
        getAuthentication();
      }
    }
  }, [projectInfo]);

  const getAppInfo = async () => {
    let info = {};
    setLoading(true);
    try {
      const appInfoResponse = await service.fetchAppInfo();
      if (appInfoResponse?.status === 200) {
        appInfoSucceeded.current = true;
        // Token is single-use; drop it after a successful config call
        clearTurnstileTokenValue();
        setTurnstileToken(null);
        info.projectConfig = appInfoResponse?.data?.data;

        Fingerprint2.get(async (component) => {
          var values = component.map((component) => {
            return component.value;
          });
          var murmur = Fingerprint2.x64hash128(values.join(""), 31);

          info.device_id = murmur;
          const geoInfo = await getGeoInfo();
          info.geoInfo = geoInfo;
          // info.projectConfig.config.TEMPLATE = "TEMPLATE_10";
          if (info.projectConfig.pubid === 50131) {
            info.projectConfig.config.TEMPLATE = "TEMPLATE_10";
          }

          dispatch(
            getProjectInfo({
              projectInfo: info,
            })
          );
          setProjectInfo(info);
          i18n.addResourceBundle(languageModal?.selectedLanguageCode, 'translation', info?.projectConfig?.config?.LANGUAGE_KEYWORDS, true, true);
        });
      } else {
        // Allow another token (e.g. after reset) to retry init
        appInfoFetched.current = false;
      }
    } catch (err) {
      appInfoRetryCount.current += 1;
      toast.error(err?.response?.data?.message, {
        position: "bottom-center",
      });
      if (appInfoRetryCount.current >= APP_INFO_MAX_RETRIES) {
        setTurnstileFailed(true);
        setLoading(false);
        return;
      }
      appInfoFetched.current = false;
      // Ask Turnstile for a fresh token so a retry can proceed
      const widgetId = turnstileWidgetId.current ?? window.__turnstileWidgetId;
      if (widgetId != null && window.turnstile) {
        clearTurnstileTokenValue();
        window.__turnstileToken = null;
        setTurnstileToken(null);
        window.turnstile.reset(widgetId);
      }
    }
  };
  const externalWebsiteLogin = async () => {
    try {
      const response = await service.updateExternalUser(appInfo, loginTicket);
      if (response?.status === 200) {
        localStorage.setItem("userId", response?.data?.data[0]?.user_id)
        dispatch(
          getUser({
            user: response?.data?.data[0]?.user_id
          })
        )
        await getAuthentication(response?.data?.data[0]?.user_id)

        if (qr) {
          navigate(`/tv?code= ${qr}`);
        }

      }
    } catch (err) {
      if (!localStorage.getItem("gid")) {
        getGuestUser();
      } else {
        getAuthentication();
      }
    }
  }

  const getGeoInfo = async () => {
    try {
      const geoInfoResponse = await service.fetchGeoInfo();
      if (geoInfoResponse?.status === 200) {
        return geoInfoResponse?.data;
      }
    } catch (err) {
      toast.error(err?.response?.data?.message, {
        position: "bottom-center",
      });
    }
  };

  const getGuestUser = async () => {
    try {
      const guestResponse = await service.guestUser(appInfo);
      if (guestResponse?.status === 200) {
        localStorage.setItem("gid", guestResponse?.data?.user_id);
        if (localStorage?.getItem("gid")) {
          getAuthentication(guestResponse?.data?.user_id);
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message, {
        position: "bottom-center",
      });
    }
  };

  const getAuthentication = async (userId) => {
    try {
      const authenticationResponse = await service.authenticate2(appInfo);
      if (
        authenticationResponse?.status === 200 &&
        authenticationResponse?.data?.message !== "Invalid user"
      ) {
        dispatch(
          getAccessToken({
            accessToken: authenticationResponse?.data?.token,
          })
        );
        if (!androidToken) {
          dispatch(
            getUserDetails({
              userDetails: authenticationResponse?.data,
            })
          );
        }
        SetIsAuthenticated(true);
        setLoading(false);

        const sessionId = await getSessionId(appInfo);
        localStorage.setItem("sessionId", sessionId);
        dispatch(
          getSessionIdValue({
            sessionId: sessionId,
          })
        );
        appInfo["sessionId"] = await sessionId;
        appInfo["accessToken"] = await authenticationResponse?.data?.token;

        if (user && authenticationResponse?.data?.parental_pin_needed === true && !parentalPinCreatedRef.current) {
          setShowParentalSetupModal(true);
        }

        if (!localStorage.getItem("applaunch")) {
          updateAppLaunch();
        }
        if (!localStorage.getItem("device_analytics")) {
          updateDeviceAnalytics(userId, appInfo);
        }
      } else {
        toast.error("Invalid User", {
          position: "bottom-center",
        });
      }
    } catch (err) { }
  };

  const updateAppLaunch = async () => {
    try {
      const response = await service.applaunchEvent("POP01", appInfo);
      if (response?.status === 200) {
        localStorage.setItem("applaunch", true);
      } else {
        localStorage.removeItem("applaunch");
      }
    } catch (err) {
      localStorage.removeItem("applaunch");
    }
  };

  const updateDeviceAnalytics = async (userId) => {
    try {
      const response = await service.analytics2(userId, appInfo);
      if (response?.status === 200) {
        localStorage.setItem("device_analytics", "true");
      } else {
        localStorage.removeItem("device_analytics");
      }
    } catch (err) {
      localStorage.removeItem("device_analytics");
    }
  };

  if (turnstileFailed || loading || (!isAuthenticated && androidToken)) {
    return <Loading />;
  }

  return (
    <div
      style={{
        backgroundColor: projectInfo?.projectConfig?.config?.BACKGROUND_COLOR
          ? projectInfo?.projectConfig?.config?.BACKGROUND_COLOR
          : projectInfo?.projectConfig?.config?.TEMPLATE === "TEMPLATE_10" ? "white" : "#10141F",
      }}
      className={`${projectInfo?.projectConfig?.config?.CUSTOM_HEADER_REQUIRED === "true" ? "customHeader" : ""} ${projectInfo?.projectConfig?.config?.TEMPLATE === "TEMPLATE_12" ? "kids" : ""}`.trim()}
    >
      {isAuthenticated && (
        notFound === "true" ? (
          <Routes>
            <Route path="*" element={<Navigate to="/404" />} />
            <Route path="/404" element={<NotFound />} />
          </Routes>


        ) : (
          <GoogleOAuthProvider clientId={projectInfo?.projectConfig?.config?.GOOGLE_CLIENT_ID ? projectInfo?.projectConfig?.config?.GOOGLE_CLIENT_ID : "test"}>
            {
              projectInfo?.projectConfig?.config?.CUSTOM_HEADER_REQUIRED === "true" &&
              <TopBanner />
            }
            <ParentalPasscodeModal
              isOpen={showParentalSetupModal}
              mode="setup"
              appInfo={{ ...appInfo, accessToken: accessTokenKids }}
              showClose={false}
              onSuccess={() => {
                parentalPinCreatedRef.current = true;
                setShowParentalSetupModal(false);
              }}
            />
            <LayoutsRoutes />
          </GoogleOAuthProvider>
        )
      )}
    </div>
  );
}

export default App;
